/**
 * Demo logins and account metadata for the in-memory mock API.
 */

const ALL_SUBJECTS = [
  "Asset",
  "AssetModel",
  "AssetCategory",
  "Location",
  "LocationCategory",
  "Maintenance",
  "Inspection",
  "Ticket",
  "Queue",
  "ProblemCategory",
  "Part",
  "Supplier",
  "Warehouse",
  "Label",
  "Event",
  "Contact",
];

// ─── Module registry ──────────────────────────────────────────────────────────

export interface MockModuleRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * All modules known to the system.
 * Names must match MENU_TO_MODULE_MAP in user-scope.store.ts.
 */
export const MOCK_AVAILABLE_MODULES: MockModuleRecord[] = [
  {
    id: "mod-1",
    name: "assets",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-2",
    name: "maintenance",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-3",
    name: "inspections",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-4",
    name: "tickets",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-5",
    name: "parts",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-6",
    name: "branches",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-7",
    name: "events",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-8",
    name: "people",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-9",
    name: "users",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    id: "mod-10",
    name: "settings",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
];

// ─── Account store ────────────────────────────────────────────────────────────

export interface MockAccountInvitation {
  id: string;
  email: string;
  invitedBy: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MockAccountRecord {
  id: string;
  name: string;
  isActive: boolean;
  isDemo: boolean;
  createdAt: string;
  updatedAt: string;
  licenseExpiryDate: string | null;
  licenseStatus: "Active" | "ExpiringSoon" | "Expired" | null;
  usersCount: number;
  branchUsersCount: number;
  domain: string | null;
  /** LuryOS host label, e.g. "acme" for acme.luryos.com */
  subdomain: string | null;
  invitations?: MockAccountInvitation[];
}

export interface MockAccountModule {
  id: string;
  accountId: string;
  moduleId: string;
  module: { id: string; name: string };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Mutable list of accounts. System admin can create / edit / delete. */
export const mockAccountsStore: MockAccountRecord[] = [
  {
    id: "1",
    name: "Demo Bäckerei GmbH",
    isActive: true,
    isDemo: true,
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
    licenseExpiryDate: "2026-12-31T00:00:00.000Z",
    licenseStatus: "Active",
    usersCount: 10,
    branchUsersCount: 5,
    domain: "baeckerei-demo.de",
    subdomain: "demo-baeckerei",
    invitations: [
      {
        id: "inv-1",
        email: "admin@demo.com",
        invitedBy: "sysadmin@luryos.com",
        role: "AccountAdmin",
        expiresAt: "2027-01-01T00:00:00.000Z",
        acceptedAt: "2024-01-05T00:00:00.000Z",
        createdAt: "2024-01-01T00:00:00.000Z",
        updatedAt: "2024-01-05T00:00:00.000Z",
      },
      {
        id: "inv-2",
        email: "manager@demo.com",
        invitedBy: "sysadmin@luryos.com",
        role: "TechnicalManager",
        expiresAt: "2027-01-01T00:00:00.000Z",
        acceptedAt: "2024-01-06T00:00:00.000Z",
        createdAt: "2024-01-02T00:00:00.000Z",
        updatedAt: "2024-01-06T00:00:00.000Z",
      },
    ],
  },
  {
    id: "2",
    name: "Tech Solutions AG",
    isActive: true,
    isDemo: false,
    createdAt: "2024-03-15T00:00:00.000Z",
    updatedAt: "2024-03-15T00:00:00.000Z",
    licenseExpiryDate: "2025-06-30T00:00:00.000Z",
    licenseStatus: "ExpiringSoon",
    usersCount: 3,
    branchUsersCount: 0,
    domain: null,
    subdomain: null,
    invitations: [],
  },
];

function buildModulesForAccount(accountId: string, activeNames: string[]): MockAccountModule[] {
  return MOCK_AVAILABLE_MODULES.map((m) => ({
    id: `am-${accountId}-${m.id}`,
    accountId,
    moduleId: m.id,
    module: { id: m.id, name: m.name },
    isActive: activeNames.includes(m.name),
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  }));
}

/**
 * Per-account module state. Mutable — system admin changes are reflected live
 * when tenant users next call /accounts/me.
 */
export const mockAccountModulesStore: Record<string, MockAccountModule[]> = {
  "1": buildModulesForAccount("1", [
    "assets",
    "maintenance",
    "inspections",
    "tickets",
    "parts",
    "branches",
    "events",
    "people",
    "users",
    "settings",
  ]),
  "2": buildModulesForAccount("2", ["assets", "maintenance", "inspections"]),
};

/**
 * Returns the AccountDetails-shaped object expected by buildUserScope.
 * Always reads live state from mockAccountModulesStore.
 */
export function getMockAccountDetails(accountId: string) {
  const account = mockAccountsStore.find((a) => a.id === accountId);
  const modules = mockAccountModulesStore[accountId] ?? [];
  return {
    id: accountId,
    name: account?.name ?? "Unknown Account",
    isActive: account?.isActive ?? true,
    isDemo: account?.isDemo ?? false,
    accountModules: modules,
  };
}

// ─── Policy builders ──────────────────────────────────────────────────────────

function buildAdminPolicies() {
  return [
    {
      id: "policy-admin",
      policy: {
        id: "policy-admin",
        name: "Full Access",
        permissions: [{ id: "perm-all", action: "manage", subject: "all" }],
      },
      createdAt: new Date().toISOString(),
    },
  ];
}

function buildTechnicalManagerPolicies() {
  const actions = ["CREATE", "READ", "UPDATE", "DELETE", "EXPORT", "IMPORT"];
  const operationalPermissions = ALL_SUBJECTS.flatMap((subject, si) =>
    actions.map((action, ai) => ({
      id: `perm-tm-${si}-${ai}`,
      action,
      subject,
    }))
  );
  const readOnlyPermissions = ["User", "Policy", "Account"].map((subject, i) => ({
    id: `perm-tm-ro-${i}`,
    action: "READ",
    subject,
  }));

  return [
    {
      id: "policy-technical-manager",
      policy: {
        id: "policy-technical-manager",
        name: "Technical Manager",
        permissions: [...operationalPermissions, ...readOnlyPermissions],
      },
      createdAt: new Date().toISOString(),
    },
  ];
}

// ─── Auth accounts ────────────────────────────────────────────────────────────

interface MockAuthAccount {
  password: string;
  authResponse: {
    token: string;
    user: {
      id: string;
      email: string;
      isEmailVerified: boolean;
      createdAt: string;
      updatedAt: string;
      user: { id: string; role: string; accountId: string };
    };
  };
  meResponse: {
    id: string;
    email: string;
    isEmailVerified: boolean;
    createdAt: string;
    updatedAt: string;
    user: {
      id: string;
      role: string;
      accountId: string;
      firstName: string;
      lastName: string;
      preferredLanguage: string;
    };
  };
  policies: ReturnType<typeof buildAdminPolicies>;
}

export const mockAuthAccounts: Record<string, MockAuthAccount> = {
  "sysadmin@luryos.com": {
    password: "demodemo",
    authResponse: {
      token: "mock-token-sysadmin",
      user: {
        id: "auth-sysadmin",
        email: "sysadmin@luryos.com",
        isEmailVerified: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: { id: "user-sysadmin", role: "SystemAdmin", accountId: "" },
      },
    },
    meResponse: {
      id: "auth-sysadmin",
      email: "sysadmin@luryos.com",
      isEmailVerified: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: {
        id: "user-sysadmin",
        role: "SystemAdmin",
        accountId: "",
        firstName: "System",
        lastName: "Admin",
        preferredLanguage: "en",
      },
    },
    policies: [],
  },
  "admin@demo.com": {
    password: "demodemo",
    authResponse: {
      token: "mock-token-admin",
      user: {
        id: "auth-1",
        email: "admin@demo.com",
        isEmailVerified: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: { id: "user-admin-1", role: "AccountAdmin", accountId: "1" },
      },
    },
    meResponse: {
      id: "auth-1",
      email: "admin@demo.com",
      isEmailVerified: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: {
        id: "user-admin-1",
        role: "AccountAdmin",
        accountId: "1",
        firstName: "Admin",
        lastName: "Demo",
        preferredLanguage: "de",
      },
    },
    policies: buildAdminPolicies(),
  },
  "manager@demo.com": {
    password: "demodemo",
    authResponse: {
      token: "mock-token-manager",
      user: {
        id: "auth-2",
        email: "manager@demo.com",
        isEmailVerified: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: { id: "user-manager-1", role: "TechnicalManager", accountId: "1" },
      },
    },
    meResponse: {
      id: "auth-2",
      email: "manager@demo.com",
      isEmailVerified: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: {
        id: "user-manager-1",
        role: "TechnicalManager",
        accountId: "1",
        firstName: "Technical",
        lastName: "Manager",
        preferredLanguage: "de",
      },
    },
    policies: buildTechnicalManagerPolicies(),
  },
};

/** Mutable session — mock client sets `current` on login/logout. */
export const mockAuthSession: { current: MockAuthAccount | null } = { current: null };

export const mockAccountConfig: {
  partPrintConfig: Record<string, unknown> | null;
  assetPrintConfig: Record<string, unknown> | null;
  locationPrintConfig: Record<string, unknown> | null;
} = { partPrintConfig: null, assetPrintConfig: null, locationPrintConfig: null };
